import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import PropertyCard from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import { Search, SlidersHorizontal, X, MapPin, Home, ArrowRight, ExternalLink, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useSEO } from '../utils/seo';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COUNTRY_CONFIG = {
  uk: {
    code: 'UK',
    name: 'United Kingdom',
    currency: '£',
    flag: '\u{1F1EC}\u{1F1E7}',
    seo: {
      title: 'Property for Sale & Rent in UK — Houses, Flats, Apartments',
      description: 'Browse UK property listings without estate agent fees. Houses, flats and apartments for sale and rent across England, Scotland, Wales & Northern Ireland. 360° tours, AI valuations. Save thousands with Shopperden.',
      keywords: 'property for sale UK, houses for rent UK, sell house without estate agent, property market UK, flats for sale, apartments to rent, Rightmove alternative, Zoopla alternative',
    },
    cities: ['London', 'Birmingham', 'Manchester', 'Leeds', 'Liverpool', 'Bristol', 'Sheffield', 'Edinburgh', 'Glasgow', 'Cardiff'],
    competitors: 'Rightmove, Zoopla, OnTheMarket',
    taxInfo: 'Stamp Duty Land Tax (SDLT)',
  },
  india: {
    code: 'IN',
    name: 'India',
    currency: '\u20B9',
    flag: '\u{1F1EE}\u{1F1F3}',
    seo: {
      title: 'Property for Sale & Rent in India — Flats, Houses, Plots',
      description: 'Browse Indian property listings without broker fees. Flats, houses, plots and villas for sale and rent across Delhi, Mumbai, Bangalore, Pune & more. 360° tours, AI valuations. Save lakhs with Shopperden.',
      keywords: 'property for sale India, flats for rent India, houses for sale Delhi, Mumbai property, Bangalore real estate, 99acres alternative, MagicBricks alternative, NoBroker alternative, property without broker',
    },
    cities: ['Mumbai', 'Delhi', 'Bangalore', 'Pune', 'Hyderabad', 'Chennai', 'Kolkata', 'Ahmedabad', 'Jaipur', 'Gurgaon'],
    competitors: '99acres, MagicBricks, NoBroker, Housing.com',
    taxInfo: 'Stamp Duty & Registration Charges',
  },
  canada: {
    code: 'CA',
    name: 'Canada',
    currency: 'C$',
    flag: '\u{1F1E8}\u{1F1E6}',
    seo: {
      title: 'Property for Sale & Rent in Canada — Houses, Condos, Apartments',
      description: 'Browse Canadian property listings without realtor commissions. Houses, condos and apartments for sale and rent across Toronto, Vancouver, Montreal & more. 360° tours, AI valuations. Save thousands with Shopperden.',
      keywords: 'property for sale Canada, houses for rent Canada, Toronto real estate, Vancouver property, condos for sale, realtor.ca alternative, property without realtor, Canadian real estate, buy house Canada',
    },
    cities: ['Toronto', 'Vancouver', 'Montreal', 'Calgary', 'Ottawa', 'Edmonton', 'Winnipeg', 'Quebec City', 'Hamilton', 'Victoria'],
    competitors: 'Realtor.ca, REW.ca, Zillow Canada',
    taxInfo: 'Land Transfer Tax',
  },
};

const CountryPropertiesPage = () => {
  const { countrySlug } = useParams();
  const config = COUNTRY_CONFIG[countrySlug];
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCity, setSelectedCity] = useState('');
  const [listingType, setListingType] = useState('');
  const [harvested, setHarvested] = useState([]);
  const [harvestedTotal, setHarvestedTotal] = useState(0);
  const [harvestedNewThisWeek, setHarvestedNewThisWeek] = useState(0);
  const [harvestedLoading, setHarvestedLoading] = useState(true);

  useSEO({
    title: config ? config.seo.title : 'Properties',
    description: config ? config.seo.description : '',
    canonical: config ? `https://shopperden.com/properties/${countrySlug}` : undefined,
    schema: config ? {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": `Properties in ${config.name}`,
      "description": config.seo.description,
      "url": `https://shopperden.com/properties/${countrySlug}`,
      "numberOfItems": properties.length,
      "itemListElement": properties.slice(0, 10).map((p, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "item": {
          "@type": "RealEstateListing",
          "name": p.title,
          "url": `https://shopperden.com/property/${p.id}`,
          "offers": { "@type": "Offer", "price": p.price, "priceCurrency": p.currency?.toUpperCase() || "GBP" }
        }
      }))
    } : undefined
  });

  const fetchProperties = useCallback(async () => {
    if (!config) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ country: config.code });
      if (selectedCity) params.append('city', selectedCity);
      if (listingType) params.append('listing_type', listingType);
      const res = await axios.get(`${API_URL}/api/properties?${params}`);
      setProperties(res.data || []);
    } catch {
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, [config, selectedCity, listingType]);

  useEffect(() => {
    fetchProperties();
  }, [countrySlug, fetchProperties]);

  // Fetch aggregated network listings (harvested from public sources)
  useEffect(() => {
    if (!config) return;
    let cancelled = false;
    setHarvestedLoading(true);
    axios
      .get(`${API_URL}/api/harvested`, { params: { country: config.code, limit: 60 } })
      .then((res) => {
        if (!cancelled) {
          const items = res?.data?.items || [];
          const total = res?.data?.total ?? items.length;
          const newThisWeek = res?.data?.new_this_week ?? 0;
          // Apply same city filter as native properties when picked
          const filtered = selectedCity
            ? items.filter((it) => (it.city || '').toLowerCase() === selectedCity.toLowerCase())
            : items;
          setHarvested(filtered);
          setHarvestedTotal(total);
          setHarvestedNewThisWeek(newThisWeek);
        }
      })
      .catch(() => !cancelled && setHarvested([]))
      .finally(() => !cancelled && setHarvestedLoading(false));
    return () => {
      cancelled = true;
    };
  }, [config, selectedCity]);

  if (!config) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <h1 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-4">Country Not Found</h1>
          <p className="text-slate-500 mb-6">Choose a market:</p>
          <div className="flex justify-center gap-4">
            <Link to="/properties/uk" className="px-6 py-3 bg-[#E07A3A] text-white font-medium tracking-wider text-sm">UK</Link>
            <Link to="/properties/india" className="px-6 py-3 bg-[#E07A3A] text-white font-medium tracking-wider text-sm">India</Link>
            <Link to="/properties/canada" className="px-6 py-3 bg-[#E07A3A] text-white font-medium tracking-wider text-sm">Canada</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid={`country-properties-${countrySlug}`}>
      <Header />

      {/* Hero */}
      <section className="bg-[#0F172A] py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <span className="text-4xl mb-3 block">{config.flag}</span>
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl text-white font-semibold mb-4">
            Property in <span className="text-[#E07A3A]">{config.name}</span>
          </h1>
          <p className="text-white/50 max-w-2xl mx-auto text-base mb-8">
            Browse verified listings with 360° tours and AI valuations. No estate agent fees — save with Shopperden.
          </p>

          {/* Country Switcher */}
          <div className="flex justify-center gap-3 mb-4">
            {Object.entries(COUNTRY_CONFIG).map(([slug, c]) => (
              <Link key={slug} to={`/properties/${slug}`}
                className={`px-5 py-2.5 text-xs tracking-wider uppercase font-bold transition-colors ${slug === countrySlug ? 'bg-[#E07A3A] text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}
                data-testid={`country-switch-${slug}`}
              >
                {c.flag} {c.name}
              </Link>
            ))}
          </div>

          {/* Aggregate total count chip */}
          <div className="flex flex-wrap justify-center items-center gap-2 mb-8" data-testid="aggregate-total-chip">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-[11px] tracking-wider uppercase text-emerald-300 font-semibold">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              {harvestedLoading ? 'Loading aggregate…' : `${(properties.length + harvestedTotal).toLocaleString()} total live listings in ${config.name} · ${harvestedTotal.toLocaleString()} from network feed + ${properties.length} direct`}
            </div>
            {!harvestedLoading && harvestedNewThisWeek > 0 && (
              <div
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#E07A3A]/15 border border-[#E07A3A]/40 rounded-full text-[11px] tracking-wider uppercase text-[#FBB076] font-bold"
                data-testid="aggregate-velocity-chip"
                title="New listings harvested in the last 7 days"
              >
                <span>↗</span>
                +{harvestedNewThisWeek.toLocaleString()} this week
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-3">
            <select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              className="h-11 bg-white/10 border border-white/20 text-white px-4 text-sm tracking-wider outline-none focus:border-[#E07A3A] min-w-[160px]"
              data-testid="city-filter"
            >
              <option value="" className="bg-[#0F172A]">All Cities</option>
              {config.cities.map(city => (
                <option key={city} value={city} className="bg-[#0F172A]">{city}</option>
              ))}
            </select>
            <select
              value={listingType}
              onChange={(e) => setListingType(e.target.value)}
              className="h-11 bg-white/10 border border-white/20 text-white px-4 text-sm tracking-wider outline-none focus:border-[#E07A3A] min-w-[140px]"
              data-testid="type-filter"
            >
              <option value="" className="bg-[#0F172A]">Buy & Rent</option>
              <option value="sale" className="bg-[#0F172A]">For Sale</option>
              <option value="rent" className="bg-[#0F172A]">For Rent</option>
            </select>
          </div>
        </div>
      </section>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">
              {selectedCity || 'All'} Properties {listingType === 'sale' ? 'for Sale' : listingType === 'rent' ? 'for Rent' : ''}
            </h2>
            <p className="text-slate-500 text-sm mt-1">{properties.length} listings in {config.name}</p>
          </div>
          <Link to="/properties" className="text-sm text-[#E07A3A] font-medium flex items-center gap-1 hover:gap-2 transition-all">
            View All Countries <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : properties.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map(p => <PropertyCard key={p.id} property={p} />)}
          </div>
        ) : (
          <div className="luxury-card p-16 text-center">
            <Home className="h-16 w-16 text-slate-200 mx-auto mb-4" />
            <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2">No Properties Yet</h3>
            <p className="text-slate-400 mb-6">Browse or list properties in {config.name}</p>
            <Link to="/register">
              <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E] tracking-wider">LIST YOUR PROPERTY</Button>
            </Link>
          </div>
        )}

        {/* Aggregated Network Listings — harvested from public sources */}
        {(harvestedLoading || harvested.length > 0) && (
          <section className="mt-16 pt-12 border-t border-slate-200" data-testid="aggregated-listings">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
              <div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-[11px] font-bold tracking-wider uppercase text-emerald-700 mb-3">
                  <ShieldCheck className="h-3 w-3" />
                  Aggregated &amp; Verified
                </span>
                <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">
                  More {config.name} listings from across the network
                </h2>
                <p className="text-slate-500 text-sm mt-1">
                  {harvestedLoading
                    ? 'Loading…'
                    : `Showing ${harvested.length} of ${harvestedTotal.toLocaleString()} aggregated ${selectedCity ? selectedCity + ' ' : ''}${config.name} listings`}
                  {' '}sourced from {config.competitors.split(',').slice(0, 2).join(' &amp; ')}.
                  Seller contact stays gated &mdash; users contact via Shopperden.
                </p>
              </div>
            </div>

            {harvestedLoading ? (
              <div className="flex justify-center py-16">
                <div className="w-6 h-6 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {harvested.map((it) => (
                  <Link
                    key={it.id || it.source_url}
                    to={`/register?intent=view-listing&id=${encodeURIComponent(it.id || '')}&country=${countrySlug}`}
                    className="group bg-white border border-slate-200 hover:border-[#E07A3A]/50 hover:shadow-lg transition-all rounded-lg overflow-hidden"
                    data-testid={`aggregated-card-${it.id || ''}`}
                  >
                    <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden">
                      {it.images && it.images[0] ? (
                        <img
                          src={it.images[0]}
                          alt={it.title}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-300">
                          <Home className="h-12 w-12" />
                        </div>
                      )}

                      {/* Subtle dark overlay so the watermark stays readable on bright photos */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-black/10 pointer-events-none" />

                      {/* Shopperden tile-watermark — diagonal repeating brand stamp */}
                      <div
                        aria-hidden="true"
                        className="absolute inset-0 pointer-events-none flex items-center justify-center"
                      >
                        <span
                          className="text-white/30 font-['Playfair_Display'] font-bold tracking-[0.3em] text-2xl sm:text-3xl rotate-[-22deg] select-none whitespace-nowrap drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]"
                        >
                          SHOPPER<span className="text-[#E07A3A]/80">DEN</span>
                        </span>
                      </div>

                      {/* Bottom-left brand badge — always visible, owns the image */}
                      <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/95 backdrop-blur-sm rounded-full text-[10px] font-bold tracking-widest uppercase text-[#1A1A1A] border border-white/40 shadow-sm">
                        <span className="w-3.5 h-3.5 rounded-sm flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #10b981 0%, #0f766e 100%)' }}>
                          <svg viewBox="0 0 64 64" className="w-2.5 h-2.5" fill="none" stroke="#ffffff" strokeLinejoin="round" strokeLinecap="round"><path d="M14 32 L32 17 L50 32 L50 50 L14 50 Z" strokeWidth="6" /></svg>
                        </span>
                        Shopper<span className="text-[#E07A3A]">den</span>
                      </span>

                      {it.verified_badge && it.verified_badge !== 'pending' && (
                        <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 bg-white/95 backdrop-blur-sm rounded-full text-[10px] font-bold tracking-wider uppercase text-emerald-700 border border-emerald-200">
                          <ShieldCheck className="h-3 w-3" />
                          {it.verified_badge}
                        </span>
                      )}
                      <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 bg-[#E07A3A] backdrop-blur-sm rounded-full text-[10px] font-bold tracking-widest uppercase text-white shadow-sm">
                        Sign up to view
                      </span>
                    </div>
                    <div className="p-4">
                      <p className="font-['Playfair_Display'] text-xl text-[#1A1A1A] leading-tight">
                        {it.price_raw || `${config.currency}${(it.price || 0).toLocaleString()}`}
                      </p>
                      <h3 className="text-sm font-medium text-slate-700 mt-1 line-clamp-2 min-h-[2.5rem]">
                        {it.title}
                      </h3>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                        {it.bedrooms && <span>{it.bedrooms} bed</span>}
                        {it.area_sqft && <span>&middot; {it.area_sqft.toLocaleString()} sqft</span>}
                        {it.city && <span>&middot; {it.city}</span>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            <p className="text-[11px] text-slate-400 mt-6 text-center">
              Listings aggregated from public sources. Sign up free to unlock full details &mdash; seller contact stays inside Shopperden&apos;s gated vault to prevent spam.
            </p>
          </section>
        )}

        {/* Popular Cities */}
        <div className="mt-16">
          <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-6">Popular Cities in {config.name}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" data-testid="popular-cities">
            {config.cities.map(city => (
              <button key={city} onClick={() => setSelectedCity(city)}
                className={`p-4 text-center border transition-colors ${selectedCity === city ? 'border-[#E07A3A] bg-[#E07A3A]/5' : 'border-slate-100 hover:border-[#E07A3A]/30'}`}
              >
                <MapPin className="h-4 w-4 text-[#E07A3A] mx-auto mb-1" />
                <span className="text-sm font-medium text-[#1A1A1A]">{city}</span>
              </button>
            ))}
          </div>
        </div>

        {/* SEO Content Block */}
        <div className="mt-16 luxury-card p-8" data-testid="seo-content">
          <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-4">
            Why Choose Shopperden for {config.name} Property?
          </h3>
          <div className="grid md:grid-cols-2 gap-6 text-sm text-slate-600 leading-relaxed">
            <div>
              <p className="mb-3">
                Shopperden is the smarter alternative to {config.competitors}. List and browse property in {config.name} without paying
                estate agent commissions. Our flat-fee model saves sellers thousands compared to traditional agents.
              </p>
              <p>
                Every listing features 360° virtual tours, AI-powered valuations, and verified users. Whether you are buying, selling,
                or renting in {config.cities.slice(0, 3).join(', ')} or anywhere else in {config.name}, Shopperden connects you directly.
              </p>
            </div>
            <div>
              <p className="mb-3">
                Our AI Professional Advisor recommends the right solicitors, surveyors, and mortgage brokers for your {config.name} property
                transaction. Track your deal from enquiry to completion with our milestone-based Deal Tracker.
              </p>
              <p>
                Shopperden Ltd is registered in England & Wales and operates across UK, India, and Canada. {config.taxInfo} calculators
                and mortgage pre-qualification tools are built in.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountryPropertiesPage;
